/* eslint-disable max-lines */
/* eslint-disable complexity */
import { Link } from '@tanstack/react-router';
import { type ColumnDef, createColumnHelper } from '@tanstack/table-core';
import { ExternalLinkIcon } from 'lucide-react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { DataTable } from '@/components/DataTable';
import { TableHeader } from '@/components/DataTable/TableHeader';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ParsedHistoryItem } from '@/lib/queries/resource';

import { DateRangeDisplay } from '../DateRangeDisplay';

const columnHelper = createColumnHelper<ParsedHistoryItem>();

const columns = [
  columnHelper.accessor('timestamp', {
    header: ({ column }) => (
      <TableHeader
        column={column}
        type="sort-only"
        headersTranslationPath="resource.history.timeline"
      />
    ),
    cell: ({ row, getValue }) => (
      <div className="flex flex-row items-center space-x-2">
        <p className="mr-2">{getValue()}</p>
        {row.original.isCurrent && (
          <div className="bg-blue-500 px-2 py-1 rounded-md text-white">
            <FormattedMessage id="resource.history.timeline.current" />
          </div>
        )}
        {row.original.deleted && (
          <div className="bg-red-500 px-2 py-1 rounded-md text-white">
            <FormattedMessage
              id={`resource.history.timeline.deleted.${row.original.trigger.type === 'commit' ? 'code' : 'cloud'}`}
            />
          </div>
        )}
      </div>
    ),
    size: 40,
  }),
  columnHelper.accessor('trigger', {
    header: () => (
      <FormattedMessage id="resource.history.timeline.trigger.header" />
    ),
    cell: ({ getValue }) => {
      const trigger = getValue();

      if (trigger.type === 'commit') {
        return (
          <div>
            {trigger.commits.map(commit => (
              <p key={commit.sha}>
                <a
                  href={commit.githubUrl}
                  className="underline text-blue-500"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FormattedMessage
                    id="resource.history.timeline.commitPushedOnRepository"
                    values={{
                      sha: commit.sha.slice(0, 8),
                      repository: commit.repository,
                    }}
                  />
                  <ExternalLinkIcon size={16} />
                </a>
              </p>
            ))}
          </div>
        );
      }

      return (
        <p>
          <FormattedMessage id="resource.history.timeline.detectedByAnyshift" />
        </p>
      );
    },
  }),
  columnHelper.accessor(
    ({ downstreamImpactChanges, propertyChanges, upstreamImpactChanges }) => ({
      createdDownstream: Object.keys(downstreamImpactChanges.created).length,
      deletedDownstream: Object.keys(downstreamImpactChanges.deleted).length,
      createdUpstream: Object.keys(upstreamImpactChanges.created).length,
      deletedUpstream: Object.keys(upstreamImpactChanges.deleted).length,
      properties: Object.keys(propertyChanges).length,
    }),
    {
      header: () => (
        <FormattedMessage id="resource.history.timeline.changes.header" />
      ),
      id: 'changes',
      cell: ({
        row: {
          original: {
            propertyChanges,
            downstreamImpactChanges,
            upstreamImpactChanges,
            trigger,
            created,
            deleted,
          },
        },
      }) => {
        if (trigger.type === 'commit') {
          return created ? (
            <FormattedMessage id="resource.history.timeline.createdOnTf" />
          ) : deleted ? (
            <FormattedMessage id="resource.history.timeline.deletedOnTf" />
          ) : (
            <FormattedMessage id="resource.history.timeline.updatedOnTf" />
          );
        }

        if (deleted) {
          return (
            <p>
              <FormattedMessage id="resource.history.timeline.resourceDeletedOnCloud" />
            </p>
          );
        }

        if (
          Object.keys(propertyChanges).length === 0 &&
          Object.keys(downstreamImpactChanges.created).length === 0 &&
          Object.keys(downstreamImpactChanges.deleted).length === 0 &&
          Object.keys(upstreamImpactChanges.created).length === 0 &&
          Object.keys(upstreamImpactChanges.deleted).length === 0
        ) {
          // This should never happen because of the filter in parseHistory.ts
          return (
            <p>
              <FormattedMessage id="resource.history.timeline.noChanges" />
            </p>
          );
        }

        return (
          <div>
            {created && (
              <p>
                <FormattedMessage id="resource.history.timeline.createdOnCloud" />
              </p>
            )}
            {Object.keys(propertyChanges).length > 0 && (
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <p className="underline decoration-dotted">
                      <FormattedMessage
                        id="resource.history.timeline.updatedProperties"
                        values={{
                          count: Object.keys(propertyChanges).length,
                        }}
                      />
                    </p>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="bg-white border text-black opacity-100 border-gray-300 dark:bg-background dark:border-border dark:text-white"
                  >
                    <div className="max-w-[700px] p-1">
                      {Object.entries(propertyChanges).map(
                        ([key, { before, after }]) => (
                          <p key={key} className="text-sm break-all">
                            {before !== undefined && after !== undefined ? (
                              <>
                                • UPDATED - {key} ={' '}
                                <span className="font-semibold dark:text-red-200 dark:bg-red-900 rounded-sm px-1 text-red-900 bg-red-200">
                                  {before}
                                </span>
                                {' → '}
                                <span className="font-semibold dark:text-green-200 dark:bg-green-900 rounded-sm px-1 text-green-900 bg-green-200">
                                  {after}
                                </span>
                              </>
                            ) : before === undefined ? (
                              <>
                                • NEW - {key} ={' '}
                                <span className="font-semibold dark:text-green-200 dark:bg-green-900 rounded-sm px-1 text-green-900 bg-green-200">
                                  {after}
                                </span>
                              </>
                            ) : (
                              <>
                                • DELETED -{' '}
                                <span className="font-semibold dark:text-red-200 dark:bg-red-900 rounded-sm px-1 text-red-900 bg-red-200">
                                  {key}={before}
                                </span>
                              </>
                            )}
                          </p>
                        ),
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {Object.keys(downstreamImpactChanges.created).length > 0 && (
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <p className="underline decoration-dotted">
                      <FormattedMessage
                        id="resource.history.timeline.newDownstreamImpacts"
                        values={{
                          count: Object.keys(downstreamImpactChanges.created)
                            .length,
                        }}
                      />
                    </p>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="bg-white border text-black opacity-100 border-gray-300 dark:bg-background dark:border-border dark:text-white"
                  >
                    <div className="max-w-[700px] p-1">
                      {Object.values(downstreamImpactChanges.created).map(
                        ({ anyshiftID, hashedID, type }) => (
                          <p key={hashedID} className="text-sm break-all">
                            • NEW (this)-[{type}]→(
                            <Link
                              to="/resource/$resourceId"
                              params={{ resourceId: hashedID }}
                            >
                              <span className="font-semibold dark:text-green-200 dark:bg-green-900 rounded-sm px-1 hover:underline text-green-900 bg-green-200">
                                {anyshiftID}
                              </span>
                            </Link>
                            )
                          </p>
                        ),
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {Object.keys(downstreamImpactChanges.deleted).length > 0 && (
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <p className="underline decoration-dotted">
                      <FormattedMessage
                        id="resource.history.timeline.deletedDownstreamImpacts"
                        values={{
                          count: Object.keys(downstreamImpactChanges.deleted)
                            .length,
                        }}
                      />
                    </p>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="bg-white border text-black opacity-100 border-gray-300 dark:bg-background dark:border-border dark:text-white"
                  >
                    <div className="max-w-[700px] p-1">
                      {Object.values(downstreamImpactChanges.deleted).map(
                        ({ anyshiftID, hashedID, type }) => (
                          <p key={hashedID} className="text-sm break-all">
                            • DELETED (this)-[{type}]→(
                            <Link
                              to="/resource/$resourceId"
                              params={{ resourceId: hashedID }}
                            >
                              <span className="font-semibold dark:text-red-200 dark:bg-red-900 rounded-sm px-1 hover:underline text-red-900 bg-red-200">
                                {anyshiftID}
                              </span>
                            </Link>
                            )
                          </p>
                        ),
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {Object.keys(upstreamImpactChanges.created).length > 0 && (
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <p className="underline decoration-dotted">
                      <FormattedMessage
                        id="resource.history.timeline.newUpstreamImpacts"
                        values={{
                          count: Object.keys(upstreamImpactChanges.created)
                            .length,
                        }}
                      />
                    </p>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="bg-white border text-black opacity-100 border-gray-300 dark:bg-background dark:border-border dark:text-white"
                  >
                    <div className="max-w-[700px] p-1">
                      {Object.values(upstreamImpactChanges.created).map(
                        ({ anyshiftID, hashedID, type }) => (
                          <p key={hashedID} className="text-sm break-all">
                            • NEW (this)←[{type}]-(
                            <Link
                              to="/resource/$resourceId"
                              params={{ resourceId: hashedID }}
                            >
                              <span className="font-semibold dark:text-green-200 dark:bg-green-900 rounded-sm px-1 hover:underline text-green-900 bg-green-200">
                                {anyshiftID}
                              </span>
                            </Link>
                            )
                          </p>
                        ),
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {Object.keys(upstreamImpactChanges.deleted).length > 0 && (
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <p className="underline decoration-dotted">
                      <FormattedMessage
                        id="resource.history.timeline.deletedUpstreamImpacts"
                        values={{
                          count: Object.keys(upstreamImpactChanges.deleted)
                            .length,
                        }}
                      />
                    </p>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="bg-white border text-black opacity-100 border-gray-300 dark:bg-background dark:border-border dark:text-white"
                  >
                    <div className="max-w-[700px] p-1">
                      {Object.values(upstreamImpactChanges.deleted).map(
                        ({ anyshiftID, hashedID, type }) => (
                          <p key={hashedID} className="text-sm break-all">
                            • DELETED (this)←[{type}]-(
                            <Link
                              to="/resource/$resourceId"
                              params={{ resourceId: hashedID }}
                            >
                              <span className="font-semibold dark:text-red-200 dark:bg-red-900 rounded-sm px-1 hover:underline text-red-900 bg-red-200">
                                {anyshiftID}
                              </span>
                            </Link>
                            )
                          </p>
                        ),
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
    },
  ),
  columnHelper.accessor(
    c =>
      c.trigger.type === 'commit'
        ? c.trigger.commits.map(({ author }) => author).join(', ')
        : undefined,
    {
      id: 'updatedBy',
      header: ({ column }) => (
        <TableHeader
          column={column}
          type="select"
          headersTranslationPath="resource.history.timeline"
        />
      ),
      cell: ({ getValue }) => {
        const authors = getValue();
        if (authors === undefined || authors.length === 0) {
          return null;
        }

        return authors;
      },
    },
  ),
] as ColumnDef<ParsedHistoryItem>[];

export const Timeline = ({
  history,
  from,
  to,
}: {
  history: ParsedHistoryItem[];
  from?: string;
  to?: string;
}): JSX.Element => {
  const filteredHistory = useMemo(
    () =>
      history.filter(
        event =>
          (from === undefined || event.timestamp >= from) &&
          (to === undefined || event.timestamp <= to),
      ),
    [history, from, to],
  );

  const changesEnabled = from !== undefined || to !== undefined;

  return (
    <>
      <div className="flex flex-row items-center mb-4 space-x-4">
        <h2 className="text-2xl">
          <FormattedMessage id="resource.history.title" />
        </h2>
        <DateRangeDisplay from={from} to={to} prefix="Changes" />
      </div>

      <DataTable
        columns={columns}
        data={filteredHistory}
        initialPagination={{
          pageSize: 25,
        }}
        headersTranslationPath="a"
        {...(changesEnabled && {
          noResultsMessage: 'resource.updatesPeriod.history.noResults',
        })}
      />
    </>
  );
};
